/**
 * Permissões efetivas do usuário logado (v7 — DESIGN-V7 §1): GET /me traz
 * `permissions: string[]` (união dos grupos de permissões; a API já expande
 * admin ⇒ lista completa — o front NÃO re-deriva política por papel).
 * O front usa can(key) só para ESCONDER itens de menu/hub/ações — quem barra
 * de verdade é a API (mapa rota→key + regras finas).
 *
 * Semântica: campo AUSENTE num perfil RESOLVIDO (back antigo, ou erro no /me)
 * ⇒ tudo permitido — a restrição só vale quando a lista VEM e a key falta.
 * Enquanto o /me ainda carrega, `ready` fica false: os consumidores escondem
 * itens gated até saber (aparecer depois é melhor que aparecer-e-sumir).
 */
import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { hasPermission, type PermissionKey, type ResolvedPermissions } from '@erp/shared';
import { profileQueryOptions } from '@/features/account/account-queries';

export interface Permissions {
  /** `true` quando o usuário pode `key` (checagem O(1) via Set do shared). */
  can: (key: PermissionKey) => boolean;
  /** `true` quando o GET /me resolveu (com lista, sem lista = back antigo, ou erro). */
  ready: boolean;
}

export function usePermissions(): Permissions {
  const profileQ = useQuery({ ...profileQueryOptions(), staleTime: 60_000 });
  const permissions = profileQ.data?.permissions;

  const resolved = useMemo<ResolvedPermissions>(
    () => (permissions ? new Set(permissions) : 'all'),
    [permissions]
  );

  const can = useCallback((key: PermissionKey) => hasPermission(resolved, key), [resolved]);

  return { can, ready: !profileQ.isPending };
}
